import { InfoCard, InfoCardAudio, InfoCardStack } from '../types/cards.types';
import { HelpCard } from '../types/helpCard.types';
import type { ConversationItem, ConversationType } from '../types/conversationItem.types';
import { translate, SupportedLanguage } from '../lib/i18n';
import type { ChatbotConfig } from '../types/chatbotConfig.types';
import api from '../lib/api';
import { LocalizationBundle } from 'types/localizationBundle.types';

let sessionToken: string | null = null;

export interface MessageResponse {
  id: string;
  conversationId: string;
  content: string;
  language: string;
  organizationId: string | null;
  chatbotConfigId: string | null;
  role: 'ASSISTANT' | 'USER' | 'OPERATOR' | 'COACH';
  createdAt: Date;
  updatedAt: Date;
  status: 'SUCCESS' | 'CANCELLED' | 'ERROR' | 'REDACTED-UNSAFE' | 'TOPIC-CHANGE' | 'MORE-INFO';
  moderationStatus: 'ACCEPTED' | 'REJECTED';
  infoCardsHidden: boolean;
  options: string[];
  executionTime: number;
}

// Token management functions
const setSessionToken = (token: string) => {
  sessionToken = token;
};

export const getSessionToken = (): string | null => {
  return sessionToken;
};

// API Functions
interface GetModerationParams {
  message: string;
  language: SupportedLanguage;
}

export const getModeration = ({ message, language }: GetModerationParams) => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: '/api/moderation',
    service: 'chat',
    method: 'POST',
    body: { message },
    token: sessionToken,
    language,
  });
};

export interface CreateAuthSessionParams {
  chatbotConfigId: string;
  password?: string | null;
}

export const createAuthSession = async ({
  chatbotConfigId,
  password,
}: CreateAuthSessionParams): Promise<CreateSessionResponse> => {
  const response = await api.apiRequest({
    endpoint: `/api/auth/chatbot/${chatbotConfigId}`,
    service: 'chat',
    method: 'POST',
    body: { password: password || null },
  });

  if (response.token) {
    setSessionToken(response.token);
  }

  return response;
};

interface CreateSessionResponse {
  id: string;
  token: string;
  error?: string;
}

export interface CreateReturnCodeResponse {
  code: string;
  expiresAt: string;
}

export interface CreateSessionParams {
  gender: string;
  consent: boolean;
  language: SupportedLanguage;
}

export const createSession = async ({
  gender,
  consent,
  language,
}: CreateSessionParams): Promise<CreateSessionResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  const response = await api.apiRequest({
    endpoint: '/api/sessions',
    service: 'chat',
    method: 'POST',
    token: sessionToken,
    body: { gender, consent },
    language,
  });

  return response;
};

export const createReturnCode = async (): Promise<CreateReturnCodeResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  const response = await api.apiRequest({
    endpoint: '/api/auth/chatbot/return-codes',
    service: 'chat',
    method: 'POST',
    body: {},
    token: sessionToken,
  });

  return response;
};

export const getConversationMessages = async (
  conversationId: string
): Promise<MessageResponse[]> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  const response = await api.apiRequest({
    endpoint: `/api/conversations/${conversationId}/messages`,
    service: 'chat',
    method: 'GET',
    token: sessionToken,
  });

  return response;
};

export interface Conversation {
  id: string;
  sessionId: string;
  language: string;
  organizationId: string;
  chatbotConfigId: string;
  createdAt: string;
  updatedAt: string;
  endedAt: string | null;
  type: 'USER' | 'OPERATOR' | 'OPERATOR_INTERNAL';
  status: 'active' | 'finalizing' | 'finished';
  messageCount: number | null;
  summary: string | null;
}

export const getSessionConversations = async (): Promise<Conversation[]> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  const response = await api.apiRequest({
    endpoint: `/api/conversations`,
    service: 'chat',
    method: 'GET',
    token: sessionToken,
  });

  return response;
};

interface CreateConversationParams {
  language?: SupportedLanguage;
  type?: ConversationType;
  selectedPersonaGroups?: string[];
}

interface CreateConversationResponse {
  id: string;
  messages: ConversationItem[];
}

export const createConversation = ({
  language,
  type,
  selectedPersonaGroups,
}: CreateConversationParams): Promise<CreateConversationResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: '/api/conversations',
    service: 'chat',
    method: 'POST',
    token: sessionToken,
    language,
    body: { type, selectedPersonaGroups },
  });
};

// Define the interface for createMessage parameters
export interface CreateMessageParams {
  conversationId: string;
  role: string;
  content: string;
  language: SupportedLanguage;
  topic: string | null;
  abortSignal?: AbortSignal;
  agent?: string;
  skipGeneration?: boolean;
  assistantContent?: string;
}

export const createMessage = async ({
  conversationId,
  role,
  content,
  topic,
  language,
  abortSignal,
  agent,
  skipGeneration,
  assistantContent,
}: CreateMessageParams) => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }
  const body = JSON.stringify({
    conversationId,
    role,
    content,
    topic,
    agent,
    skipGeneration,
    assistantContent,
  });

  const result = await api.createMessageRequest({
    endpoint: '/api/messages',
    method: 'POST',
    body,
    abortSignal,
    errorMessage: translate('common.errors.generic', language), // Define a generic error message
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
      'X-App-Language': language,
    },
  });

  // If request was aborted or has an error, handle accordingly
  if (result.aborted) {
    return { aborted: true };
  }

  if (result.error) {
    // Return error response that matches assistant message format
    return {
      id: Date.now(),
      author: 'ASSISTANT',
      content: result.message,
      time: new Date().toUTCString(),
      options: [],
      isError: true,
    };
  }

  return result;
};

interface SubmitMessageFeedbackResponse {
  messageId: string;
  thumbs: 'up' | 'down';
  freeText: string;
}

interface SubmitMessageFeedbackParams {
  messageId: string;
  thumbs: 'up' | 'down';
  freeText: string;
  language?: SupportedLanguage;
}

export const submitMessageFeedback = ({
  messageId,
  thumbs,
  freeText,
  language,
}: SubmitMessageFeedbackParams): Promise<SubmitMessageFeedbackResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: `/api/messages/${messageId}/feedback`,
    service: 'chat',
    method: 'POST',
    body: { thumbs, freeText },
    token: sessionToken,
    language,
  });
};

interface SubmitSessionFeedbackParams {
  rating: number | null;
  feedbackText: string;
  consentQuote: boolean;
  language?: SupportedLanguage;
}

interface SubmitSessionFeedbackResponse {
  sessionId: string;
  rating: number;
}

// Session Feedback API Functions
export const submitSessionFeedback = ({
  rating,
  feedbackText,
  consentQuote,
  language,
}: SubmitSessionFeedbackParams): Promise<SubmitSessionFeedbackResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: `/api/sessions/feedback`,
    service: 'chat',
    method: 'POST',
    body: { rating, feedbackText, consentQuote },
    token: sessionToken,
    language,
  });
};

interface UpdateMessageModerationStatusParams {
  messageId: string;
  moderationStatus: 'ACCEPTED' | 'REJECTED';
  language?: SupportedLanguage;
}

interface UpdateMessageModerationStatusResponse {
  id: string;
}

export const updateMessageModerationStatus = ({
  messageId,
  moderationStatus,
  language,
}: UpdateMessageModerationStatusParams): Promise<UpdateMessageModerationStatusResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: `/api/messages/${messageId}/moderation`,
    service: 'chat',
    method: 'PUT',
    body: { moderationStatus },
    token: sessionToken,
    language,
  });
};

interface HideInfoCardsResponse {
  messageId: string;
  infoCardsHidden: boolean;
}

interface HideInfoCardsParams {
  messageId: string;
  language?: SupportedLanguage;
}

export const hideInfoCardsForMessage = ({
  messageId,
  language,
}: HideInfoCardsParams): Promise<HideInfoCardsResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: `/api/messages/${messageId}/hide-info-cards`,
    service: 'chat',
    method: 'POST',
    token: sessionToken,
    language,
  });
};

export interface SubmitResearchConsentParams {
  conversationId: string;
  consent: boolean;
  language?: SupportedLanguage;
}

export const submitResearchConsent = ({
  conversationId,
  consent,
  language,
}: SubmitResearchConsentParams): Promise<void> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: `/api/conversations/${conversationId}/research-consent`,
    service: 'chat',
    method: 'POST',
    body: { consent },
    token: sessionToken,
    language,
  });
};

export interface generateOperatorReportParams {
  conversationId: string;
  language: SupportedLanguage;
}

export const generateOperatorReport = async ({
  conversationId,
  language,
}: generateOperatorReportParams): Promise<{ report: string }> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.authenticatedApiRequest({
    endpoint: `/api/conversations/${conversationId}/operator-report`,
    service: 'chat',
    method: 'POST',
    token: sessionToken,
    language,
  });
};

export interface TranscribeAudioParams {
  audioFile: Blob;
  fileName: string;
  language: SupportedLanguage;
  conversationId: string;
}

export interface TranscribeAudioResponse {
  text: string;
  language?: string;
  duration?: number;
  originalFileName?: string;
  fileSize?: number;
}

/**
 * Transcribe audio file to text using STT
 */
export const transcribeAudio = async ({
  audioFile,
  fileName,
  language,
  conversationId,
}: TranscribeAudioParams): Promise<TranscribeAudioResponse> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  const formData = new FormData();

  formData.append('audio', audioFile, fileName);
  formData.append('conversationId', conversationId);

  return api.authenticatedFileUploadRequest({
    endpoint: '/api/messages/transcribe',
    service: 'chat',
    method: 'POST',
    body: formData,
    token: sessionToken,
    language,
  });
};

export interface AISurveyAnswerParams {
  questions: string[];
  answers: string[];
  language?: SupportedLanguage;
}

export interface HelpSource {
  name: string;
  description: string;
  contacts: Array<{
    type: 'url' | 'phone';
    value: string;
  }>;
  source: 'online' | 'offline';
}

export interface AISurveyAnswerResponse {
  victim_profile: string;
  crime_overview: string;
  immediate_actions: string[];
  help_sources: HelpSource[];
}

export const getAISurveyFeedback = async ({
  conversationId,
  questions,
  answers,
  language = 'fi',
}: AISurveyAnswerParams & {
  conversationId: string;
}): Promise<AISurveyAnswerResponse> => {
  // Format survey answers as string for backend processing
  // Only include questions that have been answered (exclude empty/undefined answers)
  const surveyAnswersString = questions
    .map((question, index) => ({ question, answer: answers[index] }))
    .filter(({ answer }) => answer && answer.length > 0)
    .map(
      ({ question, answer }) =>
        `Q: ${question}\nA: ${Array.isArray(answer) ? answer.join(', ') : answer}`
    )
    .join('\n\n');

  // Send the message with simplified agent data
  const messageResponse = await createMessage({
    conversationId,
    role: 'USER',
    content: surveyAnswersString,
    topic: 'survey',
    language,
    agent: 'feedback_agent',
  });

  // Check if the response is an error
  if (messageResponse.isError) {
    throw new Error(messageResponse.content || 'Failed to get AI feedback');
  }

  if (!messageResponse || typeof messageResponse !== 'object') {
    throw new Error('Invalid response format from backend');
  }

  if (!('content' in messageResponse) || typeof messageResponse.content !== 'string') {
    throw new Error('Expected message response with content field');
  }

  let response: AISurveyAnswerResponse;
  try {
    response = JSON.parse(messageResponse.content);
  } catch {
    throw new Error('Failed to parse agent response from message content');
  }

  if (
    !response.victim_profile ||
    !response.crime_overview ||
    !Array.isArray(response.immediate_actions) ||
    !Array.isArray(response.help_sources)
  ) {
    throw new Error('Missing required fields in structured response');
  }

  return response;
};

// Chatbot Config API Functions
interface GetChatbotConfigParams {
  configId: string;
}

export const getChatbotConfig = ({ configId }: GetChatbotConfigParams): Promise<ChatbotConfig> => {
  return api.apiRequest({
    endpoint: `/api/chatbot-configs/${configId}`,
    method: 'GET',
    service: 'chat',
  });
};

interface GetLocalizationBundleParams {
  configId: string;
}

export const getLocalizationBundles = ({
  configId,
}: GetLocalizationBundleParams): Promise<LocalizationBundle[]> => {
  return api.apiRequest({
    endpoint: `/api/chatbot-configs/${configId}/localizations`,
    method: 'GET',
    service: 'chat',
  });
};

interface GetInfoCardStackParams {
  language: SupportedLanguage;
  stackId: string;
}

export const getInfoCardStack = ({
  language,
  stackId,
}: GetInfoCardStackParams): Promise<InfoCardStack> => {
  return api.apiRequest({
    endpoint: `/api/info-card-stacks/${stackId}`,
    method: 'GET',
    service: 'chat',
    language,
  });
};

interface GetInfoCardStacksParams {
  language: SupportedLanguage;
}

export const getInfoCardStacks = ({
  language,
}: GetInfoCardStacksParams): Promise<InfoCardStack[]> => {
  return api.apiRequest({
    endpoint: `/api/info-card-stacks`,
    method: 'GET',
    service: 'chat',
    language,
  });
};

interface GetInfoCardsParams {
  language: SupportedLanguage;
  stackId: string;
}

export const getInfoCards = ({ language, stackId }: GetInfoCardsParams): Promise<InfoCard[]> => {
  return api.apiRequest({
    endpoint: `/api/info-card-stacks/${stackId}/info-cards`,
    method: 'GET',
    service: 'chat',
    language,
  });
};

interface GetInfoCardAudiosParams {
  language: SupportedLanguage;
  stackId: string;
}

export const getInfoCardAudios = ({
  language,
  stackId,
}: GetInfoCardAudiosParams): Promise<InfoCardAudio[]> => {
  return api.apiRequest({
    endpoint: `/api/info-card-stacks/${stackId}/info-card-audios`,
    method: 'GET',
    service: 'chat',
    language,
  });
};

interface GetHelpCardParams {
  language: SupportedLanguage;
  helpCardId: string;
}

export const getHelpCard = async ({
  language,
  helpCardId,
}: GetHelpCardParams): Promise<HelpCard> => {
  if (!sessionToken) {
    throw new Error('No session token available');
  }

  return api.apiRequest({
    endpoint: `/api/help-cards/${helpCardId}`,
    method: 'GET',
    service: 'chat',
    token: sessionToken,
    language,
  });
};
